<template>
  <Drawer
    :id="drawerId"
    :title="title"
  >
    <div class="px-4 pt-4">
      <mcs-inline-nav
        v-if="views.length > 1"
        v-model="view"
        class="mb-4"
        :items="views"
      />
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row v-if="showPresets">
          <v-col cols="12">
            <v-text-field
              v-model="search"
              outlined
              placeholder="Search"
              validate-on-blur
              hide-details="auto"
              clearable
            />
          </v-col>
          <v-col
            v-for="(category, i) in filteredCategories"
            :key="i"
            cols="12"
          >
            <mcs-image-card
              :title="category.name"
              :url="category.imageUrl"
              thumbnail
              img-width="82"
              class="hover-pointer"
              force-row-flex
              :subtitle="`Available in ${core_products_func(category.id).length} sizes`"
              @click="showSizes(category.id)"
            >
              <template #append>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
            </mcs-image-card>
          </v-col>
        </v-row>
        <template v-else-if="showCustom">
          <v-row>
            <v-col
              v-if="!isChildOrParentMasterAccount"
              cols="12"
            >
              <mcs-info-card
                title="Read before you design"
                subtitle="Custom sized designs cannot be printed on our website. Use them only if you plan to design and download. To order prints, please select a preset size. For questions, contact us at support@mycreativeshop.com"
                prepend-icon="mdi-information"
              ></mcs-info-card>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="unitId"
                outlined
                label="Unit"
                class="min-width pt-0 mt-0"
                :items="units"
                item-value="id"
                item-text="name"
                hide-details="auto"
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="width"
                outlined
                label="Width"
                validate-on-blur
                hide-details="auto"
                :rules="[$rules.required, $rules.double]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="height"
                outlined
                label="Height"
                validate-on-blur
                hide-details="auto"
                :rules="[$rules.required, $rules.double]"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="showBleed"
                outlined
                :items="bleedItems"
                label="Include bleed"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row v-if="showBleed">
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="top"
                outlined
                validate-on-blur
                label="Top"
                hide-details="auto"
                :rules="[$rules.double]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="left"
                label="Left"
                validate-on-blur
                outlined
                hide-details="auto"
                :rules="[$rules.double]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="bottom"
                outlined
                validate-on-blur
                hide-details="auto"
                label="Bottom"
                :rules="[$rules.double]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="right"
                outlined
                label="Right"
                validate-on-blur
                hide-details="auto"
                :rules="[$rules.double]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="pages"
                :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                label="# of pages"
                :rules="[$rules.required]"
                outlined
              />
            </v-col>
          </v-row>
        </template>
      </v-form>
    </div>
    <Portal :to="`drawerAppend-${drawerId}`">
      <div
        v-if="showCustom"
        class="pa-4"
      >
        <mcs-btn
          large
          color="black"
          block
          :loading="saving"
          @click="save"
        >
          Start Designing
        </mcs-btn>
      </div>
    </Portal>
  </Drawer>
</template>

<script>
import Drawer from '@/components/layout/Drawer';
import { DRAWERS, ENUMS } from '@/utils/constants';
import collections from '@/utils/mixins/collections';
import { sortBy } from 'lodash';

import { mapGetters } from 'vuex';
export default {
  components: { Drawer },
  mixins: [collections],
  props: {},
  data: () => ({
    saving: false,
    valid: false,
    drawerId: DRAWERS.BLANK_CANVAS,
    search: '',
    isTemplate: false,
    width: null,
    height: null,
    unitId: null,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    name: '',
    showBleed: true,
    pages: 2,
    view: 'presets',
    bleedItems: [
      { text: 'Yes', value: true },
      { text: 'No', value: false }
    ]
  }),
  computed: {
    views() {
      const list = [
        { title: 'Presets', id: 'presets', hide: !this.categories.length },
        { title: 'Custom', id: 'custom' }
      ];
      return list.filter((x) => !x.hide);
    },
    title() {
      return 'New design';
    },
    showCustom() {
      return this.view === 'custom';
    },
    showPresets() {
      return this.view === 'presets';
    },
    category() {
      return this.categories.find((x) => x.id === this.categoryId);
    },
    filteredCategories() {
      let list = this.categories;
      if (this.search) {
        const search = this.search.toLowerCase();
        const coreProductsCategoryIds = this.all_core_products
          .filter((x) => x.name.toLowerCase().includes(search))
          .map((x) => x.categoryId);

        list = list.filter(
          (x) => x.name.toLowerCase().includes(search) || coreProductsCategoryIds.includes(x.id)
        );
      }
      return sortBy(list, 'name');
    },
    ...mapGetters({
      folders: 'admin/collections/all_folders',
      initialData: 'drawers/data',
      top_drawer: 'drawers/topDrawer',
      drawerOpenedAt: 'drawers/lastOpenedAt',
      categories: 'templates/categories',
      core_products_func: 'templates/core_products',
      all_core_products: 'templates/all_core_products',
      units: 'lookups/units',
      prefersMetric: 'user/prefersMetric',
      isChildOrParentMasterAccount: 'user/isChildOrParentMasterAccount'
    })
  },
  watch: {
    drawerOpenedAt(val, oldVal) {
      if (this.drawerId === this.top_drawer?.id) {
        this.unitId = this.prefersMetric ? ENUMS.UNITS.MILLIMETERS : ENUMS.UNITS.INCHES;
        this.setBleed();
        this.saving = false;
        const initial = this.initialData(this.drawerId) || {};
        this.isTemplate = initial.isTemplate;
        this.view = this.views[0].id;
        this.$nextTick(() => {
          const categoryId = initial.categoryId || null;
          if (categoryId) {
            this.showSizes(categoryId);
          }
        });
      }
    },
    unitId(val, oldVal) {
      if (val !== oldVal) {
        this.setBleed();
      }
    }
  },
  methods: {
    cancel() {
      this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
    },
    showSizes(categoryId) {
      this.$store.dispatch('drawers/open', {
        drawerId: DRAWERS.BLANK_CANVAS_PRESET,
        data: { categoryId, isTemplate: this.isTemplate }
      });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.saving = true;
        this.$api
          .post('collections/getTemplateEditorUrl', {
            isCustom: this.showCustom,
            collectionId: this.isTemplate ? this.collectionId : null,
            folderId: this.parentId,
            unitId: this.unitId,
            height: this.height,
            width: this.width,
            top: this.showBleed ? this.top : 0,
            bottom: this.showBleed ? this.bottom : 0,
            left: this.showBleed ? this.left : 0,
            right: this.showBleed ? this.right : 0,
            pages: this.pages
          })
          .then((response) => {
            window.open(response.data.url, '_blank');
            this.cancel();
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    setBleed() {
      switch (this.unitId) {
        case ENUMS.UNITS.INCHES:
          this.top = ENUMS.BLEED.INCHES;
          this.bottom = ENUMS.BLEED.INCHES;
          this.left = ENUMS.BLEED.INCHES;
          this.right = ENUMS.BLEED.INCHES;
          break;
        case ENUMS.UNITS.MILLIMETERS:
          this.top = ENUMS.BLEED.MILLIMETERS;
          this.bottom = ENUMS.BLEED.MILLIMETERS;
          this.left = ENUMS.BLEED.MILLIMETERS;
          this.right = ENUMS.BLEED.MILLIMETERS;
          break;
        default:
          this.top = 0;
          this.bottom = 0;
          this.left = 0;
          this.right = 0;
          break;
      }
    }
  }
};
</script>
